/**
 * Données propres à Mon compte v2. Les autres appels sont ceux de la v1, par ses hooks
 * (hooks/useApiQueries.ts) : mêmes routes, mêmes clés de cache, donc aucun appel en plus.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useListCoaches, useListLocations, useListSites, useListVisits } from 'hooks/useApiQueries';
import { useUserContext } from 'providers/useUserContext';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import type { MindbodyContract } from 'types/contract';
import type { ProfileCopy, ReferralPublicConfig } from 'types/referral';
import { HTTP } from 'utils/http';
import { resolveProfileCopy } from 'utils/referralProfileCopy';
import { brandForSite, injectedBrands, knownBrandNames, studioDisplayName, type BrandInfo } from './lib/brands';
import { paginateHistory, type VisitsHistoryPage } from './lib/visits';

/**
 * Historique paginé de « Voir tous mes cours » (SPEC VC2, § 5.3) :
 * GET client/visits/history?page&perPage[&from&to] → { items, total, page, perPage, months }.
 *
 * Tant que la route ne répond pas (serveur du site sans la route, 404 `rest_no_route` ou
 * `UxV2Disabled`, ou erreur), la page est calculée dans le navigateur à partir de GET client/visits,
 * déjà chargé pour les cours à venir : même forme, mêmes règles (passé, sans VisitType -1, du plus
 * récent au plus ancien).
 */
export function useVisitsHistory(params: { page: number; perPage: number; from?: string; to?: string }) {
  const { user, isLoggedIn } = useUserContext();
  const { data: visits, isLoading: visitsLoading } = useListVisits() || {};

  const query = useQuery({
    queryKey: ['/client/visits/history', user?.Id, params.page, params.perPage, params.from ?? '', params.to ?? ''],
    queryFn: () => {
      const search: Record<string, string> = { page: String(params.page), perPage: String(params.perPage) };
      if (params.from) search.from = params.from;
      if (params.to) search.to = params.to;
      return HTTP.get<VisitsHistoryPage>('/client/visits/history', search).send();
    },
    enabled: isLoggedIn,
    retry: false,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });

  const fallback = query.isError || (query.data !== undefined && !Array.isArray(query.data?.items));

  const data = useMemo<VisitsHistoryPage | undefined>(() => {
    if (!fallback) return query.data;
    if (!visits) return undefined;
    return paginateHistory(visits, params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallback, query.data, visits, params.page, params.perPage, params.from, params.to]);

  return {
    data,
    isLoading: fallback ? visitsLoading : query.isLoading,
    isFetching: query.isFetching,
    /** true : page calculée dans le navigateur (route serveur absente ou en erreur). */
    isFallback: fallback,
  };
}

/** Clé et chemin de l'historique Zingfit de la v2 (useStrictZingfitVisits) : jamais la clé de la v1. */
export const STRICT_ZINGFIT_PATH = '/client/zingfit-visits?strict=1';

/** Lecture de GET client/zingfit-visits?strict=1 (requête de useStrictZingfitVisits, et « Réessayer »). */
export const readStrictZingfitVisits = () =>
  HTTP.get<Record<string, number>>('/client/zingfit-visits', { strict: '1' }).send();

/**
 * Historique Zingfit (Stretch) de « Mes chiffres » v2 : GET client/zingfit-visits?strict=1. Même réponse que pour la v1
 * (hooks/useApiQueries useZingfitVisits : { SiteId: nombre }) quand tout est lu ; mais un site de Paris du membre dont
 * l'historique n'a pas pu être lu (Stretch en échec, rien en base) rend une erreur 503 au lieu d'être omis en silence
 * (Api/Routes/Client.php getZingfitVisits). Sans cela, la v2 donnait les visites Mindbody seules pour définitives chez
 * un ancien membre Zingfit (relecture du 09/10 de la décision de DA : chiffres indisponibles). La v1 n'envoie pas
 * `strict` : inchangée. Clé propre ([chemin et paramètre, client]) : jamais la réponse de la v1 en cache à sa place.
 */
export function useStrictZingfitVisits() {
  const { user, isLoggedIn } = useUserContext();
  return useQuery({
    queryKey: [STRICT_ZINGFIT_PATH, user?.Id],
    queryFn: readStrictZingfitVisits,
    retry: false,
    enabled: isLoggedIn,
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60,
  });
}

/**
 * Catalogue des contrats (GET sale/contracts, lieu 1), même clé de cache que useListContracts : chargé
 * seulement quand « Mon abonnement » en a besoin (lib/subscription.ts needsCatalogue), soit presque jamais :
 * un appel de moins pour la plupart des membres.
 */
export function useContractCatalogue(enabled: boolean) {
  return useQuery({
    queryKey: ['/sale/contracts', 1],
    queryFn: () => HTTP.get<MindbodyContract[]>('/sale/contracts', {}).send(),
    retry: false,
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

/**
 * = UserProfile.tsx useReferralPublicConfig() : config publique du parrainage, même clé de cache
 * (une seule requête pour le lien et les textes de l'encart).
 */
export const useReferralPublicConfig = () =>
  useQuery({
    queryKey: ['/referral/active-campaigns'],
    queryFn: () => HTTP.get<ReferralPublicConfig>('/referral/active-campaigns').send(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  }).data;

/** = UserProfile.tsx useProfileCopy() */
export const useProfileCopy = (): ProfileCopy => {
  const referralConfig = useReferralPublicConfig();
  return useMemo(() => resolveProfileCopy(referralConfig), [referralConfig]);
};

/**
 * = UserProfile.tsx useReferralLink() : lien de parrainage du membre, avec la campagne par défaut si
 * elle est utilisable (`?campaign=&clientId=`), sinon au format historique (`?referral=<base64>`).
 */
export const useReferralLink = () => {
  const { user } = useUserContext();
  const { pathname } = useLocation();
  const referralConfig = useReferralPublicConfig();

  return useMemo(() => {
    if (!user) return null;
    const { UniqueId, Id } = user;
    const baseUrl = window.location.href.split(pathname).shift() ?? '';

    if (!baseUrl) return null;

    const defaultCampaignId = referralConfig?.defaultCampaignId ?? null;
    const isDefaultUsable =
      !!defaultCampaignId && !!referralConfig?.campaigns?.some((c) => c.id === defaultCampaignId);

    if (isDefaultUsable) {
      const campaign = encodeURIComponent(defaultCampaignId);
      const clientId = encodeURIComponent(String(Id));
      return `${baseUrl}/account?campaign=${campaign}&clientId=${clientId}`;
    }

    const encodedData = btoa(JSON.stringify({ UniqueId, Id }));
    return `${baseUrl}/account?referral=${encodedData}`;
  }, [user, pathname, referralConfig]);
};

/**
 * Noms du studio et du coach d'une visite, comme la v1 (Visits/VisitsHistory.tsx) : studio = même Id
 * et même site, coach = même Id et même site. Données de GET site/locations et GET staff/coaches
 * (mêmes clés de cache que la v1). Le studio est affiché sous son nom court, sans le préfixe de marque
 * (« dynamo Opéra » → « Opéra »), comme le planning et la fiche cours ; la marque d'une visite d'un
 * autre site est portée par son étiquette.
 */
export function useVisitLookup() {
  const { data: coaches } = useListCoaches();
  const { data: locations } = useListLocations();
  const { data: sites } = useListSites();
  const brands = useMemo(() => injectedBrands(), []);
  const currentSiteId = typeof mondayMindbody !== 'undefined' ? mondayMindbody?.siteId : undefined;

  const brandNames = useMemo(
    () =>
      knownBrandNames({
        brands,
        brandName: typeof mondayMindbody !== 'undefined' ? mondayMindbody?.customization?.brandName : undefined,
      }),
    [brands],
  );
  /** Nom court d'un studio (sans préfixe de marque). */
  const studioName = useCallback((name: string | undefined | null) => studioDisplayName(name, brandNames), [brandNames]);
  const studioOf = useCallback(
    (siteId: number, locationId: number | undefined) =>
      studioName(locations?.find((l) => l.Id === locationId && Number(l.SiteID) === Number(siteId))?.Name),
    [locations, studioName],
  );
  const coachOf = useCallback(
    (siteId: number, staffId: number | undefined) =>
      coaches?.find((c) => c.Id === staffId && String(c.SiteId) === String(siteId))?.FirstName?.trim() ?? '',
    [coaches],
  );
  const brandOf = useCallback(
    (siteId: number): BrandInfo => brandForSite(siteId, { sites, brands, currentSiteId }),
    [sites, brands, currentSiteId],
  );

  return { studioOf, studioName, coachOf, brandOf, currentSiteId, sites };
}

/** = UserProfile.tsx : défilement vers l'ancre de l'URL (#parrainage…), après le premier rendu. */
export function useScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    const timer = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }, 500);
    return () => clearTimeout(timer);
  }, [location]);
}
